import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { LocationAssignment } from 'src/app/models/locationAssignment.model';
import { GroupLookup } from 'src/app/models/lookup.model';
import { TaskAssignment, TaskTimeRange } from 'src/app/models/taskAssignment.model';
import { LocationService } from 'src/app/services/location.service';
import { TaskService } from 'src/app/services/task.service';
import { TaskAssignmentService } from 'src/app/services/taskAssignment.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ArrayFormField, FormField, SingleSelectionFormField, TimeFormField } from 'src/app/shared/form/formField.model';
import { isBefore, isAfter, addDays } from 'date-fns';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface ITaskAssignmentFormDialogData extends IFormDialogData {
  sensorAssignmentId: string;
  sampleStartTime: number;
  sampleEndTime: number | null;
  taskTimeRanges: TaskTimeRange[];
}

@Component({
  standalone: false,
  selector: 'app-task-assignment-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss']
})
export class TaskAssignmentDialogComponent extends FormComponent<TaskAssignment> implements OnInit {

  locationGroups: GroupLookup[];
  taskGroups: GroupLookup[];
  SECONDS_IN_DAY = 86400;

  constructor(dialogRef: MatDialogRef<TaskAssignmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ITaskAssignmentFormDialogData,
    service: TaskAssignmentService,
    protected injector: Injector,
    private taskService: TaskService,
    private locationService: LocationService
  ) {
    super(dialogRef, service, data, "Task", injector);
  }

  ngOnInit() {
    this.isFormLoading = true;
    forkJoin({
      entity: this.getEntity(),
      locations: this.locationService.getGroupLookups(),
      tasks: this.taskService.getGroupLookups(),
    }).subscribe(
      result => {
        this.entity = result.entity;
        this.locationGroups = result.locations;
        this.taskGroups = result.tasks;
        this.initModel();
        this.initFields();
        this.isFormLoading = false;
      },
      error => {
        this.toastService.showError(error);
        this.isFormLoading = false;
      }
    );
  }

  getNewObject(): TaskAssignment {
    return new TaskAssignment();
  }

  initModel(): void {
    this.entity.locationAssignments = this.entity.locationAssignments || [];

    if (!this.isAdd) {
      this.entity.startTime = this.dateFormatService.formatTimestampToString(this.entity.startDate, this.dateFormatService.dateToTimeFormat);
      this.entity.endTime = this.dateFormatService.formatTimestampToString(this.entity.endDate, this.dateFormatService.dateToTimeFormat);
    }
  }

  validate(): boolean {
    if (this.timeError) {
      this.toastService.showError("", this.timeError);
      return false;
    }
    return true;
  }

  onBeforeSave(model: TaskAssignment): void {
    model.isFinished = true;

    if (this.isAdd) {
      model.sensorAssignmentId = this.data.sensorAssignmentId;
    }

    const updateAssignments = (assignments, startDate, endDate) => {
      if (assignments && assignments.length > 0) {
        assignments = assignments.filter(assignment => Object.keys(assignment).length > 0);
        if (assignments.length > 0) {
          assignments.forEach(assignment => {
            if (assignment.locationId) {
              assignment.startDate = startDate;
              assignment.endDate = endDate;
            }
          });
        } else {
          return null;
        }
      } else {
        return null;
      }
      return assignments;
    };

    model.locationAssignments = updateAssignments(model.locationAssignments, model.startDate, model.endDate);
    // PPE is assigned per sample; a task loaded for edit may still carry one, which the API can't store.
    delete model.ppeAssignments;
  }


  initLocationFields(): FormField[][] {
    const locationFields: FormField[][] = [];
    const locationAssignmentsFields = this.createLocationAssignmentFields();

    if (this.entity.locationAssignments.length === 0) {
      locationFields.push(locationAssignmentsFields);
      this.entity.locationAssignments.push(new LocationAssignment());
    }
    else {
      this.entity.locationAssignments.forEach(locationAssignment => {
        locationFields.push(locationAssignmentsFields);
      });
    }

    return locationFields;
  }

  createLocationAssignmentFields(): FormField[] {
    const locationAssignmentsFields = [
      new SingleSelectionFormField({
        property: nameOf<LocationAssignment>(x => x.locationId),
        name: "Location",
        groups: this.locationGroups,
        columnSpan: new ColumnSpan(1, 1),
        cssClass: "multiple-row"
      })
    ];

    return locationAssignmentsFields;
  }

  validateTime(): void {
    const startTimeDate = this.dateFormatService.convertTimestampToDate(this.data.sampleStartTime);
    const endTimeDate = this.data.sampleEndTime ? this.dateFormatService.convertTimestampToDate(this.data.sampleEndTime) : null;

    if (this.entity.startTime && this.entity.endTime) {
      const startFieldDate = this.convertTimeFieldToDate(this.entity.startTime, startTimeDate, endTimeDate);
      const endFieldDate = this.convertTimeFieldToDate(this.entity.endTime, startTimeDate, endTimeDate);

      let overlappingRange = null;
      const isStartTimeGreater = startFieldDate > endFieldDate;
      const isValidRange = this.isWithinSample(startFieldDate, startTimeDate, endTimeDate) && this.isWithinSample(endFieldDate, startTimeDate, endTimeDate);

      this.data.taskTimeRanges.forEach(task => {
        const taskStartDate = this.dateFormatService.convertTimestampToDate(task.startDate);
        const taskEndDate = this.dateFormatService.convertTimestampToDate(task.endDate);

        if ((startFieldDate < taskEndDate && endFieldDate > taskStartDate) ||
          (startFieldDate >= taskStartDate && startFieldDate < taskEndDate) ||
          (endFieldDate > taskStartDate && endFieldDate <= taskEndDate)) {
          overlappingRange = { start: new Date(this.dateFormatService.formatTimestampToString(task.startDate, this.dateFormatService.dateToStringWithTimeFormat)), end: new Date(this.dateFormatService.formatTimestampToString(task.endDate, this.dateFormatService.dateToStringWithTimeFormat)) };
        }
      });

      if (!isValidRange) {
        const sampleStartLabel = this.formatDateAMPM(new Date(this.dateFormatService.formatTimestampToString(this.data.sampleStartTime, this.dateFormatService.dateToStringWithTimeFormat)));
        this.timeError = endTimeDate
          ? `Time must be between ${sampleStartLabel} and ${this.formatDateAMPM(new Date(this.dateFormatService.formatTimestampToString(this.data.sampleEndTime, this.dateFormatService.dateToStringWithTimeFormat)))}.`
          : `Time must be after ${sampleStartLabel}.`;
      } else if (isStartTimeGreater) {
        this.timeError = 'Start time cannot be greater than end time.';
      } else if (overlappingRange) {
        this.timeError = `The selected time range overlaps with an existing task from ${this.formatDateAMPM(overlappingRange.start)} to ${this.formatDateAMPM(overlappingRange.end)}.`;
      } else {
        this.timeError = null;
        this.entity.startDate = this.dateFormatService.convertDateToTimestamp(startFieldDate);
        this.entity.endDate = this.dateFormatService.convertDateToTimestamp(endFieldDate);
      }
    }
  }

  private isWithinSample(date: Date, sampleStart: Date, sampleEnd: Date | null): boolean {
    if (!sampleEnd) {
      return date >= sampleStart;
    }
    if (sampleStart > sampleEnd) {
      return date >= sampleStart || date < sampleEnd;
    }
    return date >= sampleStart && date <= sampleEnd;
  }

  formatDateAMPM(date: Date): string {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
  }

  convertTimeFieldToDate(timeField: string, startTimeDate: Date, endTimeDate: Date | null): Date {
    const [hours, minutes] = timeField.split(':').map(Number);

    let date = this.cloneAndSetTime(startTimeDate, hours, minutes);

    if (isBefore(date, startTimeDate)) {
      date = addDays(date, 1);
    }

    if (endTimeDate && isAfter(date, endTimeDate)) {
      date = this.cloneAndSetTime(endTimeDate, hours, minutes);
    }

    return date;
  }

  cloneAndSetTime(date: Date, hours: number, minutes: number): Date {
    const d = new Date(date.getTime());
    d.setHours(hours, minutes, 0, 0);
    return d;
  }

  initFields() {
    const locationFields = this.initLocationFields();

    this.fields = [
      new SingleSelectionFormField({ property: nameOf<TaskAssignment>(x => x.taskId), name: "Task Name", groups: this.taskGroups, columnSpan: new ColumnSpan(1, 1) }),

      new TimeFormField({ property: nameOf<TaskAssignment>(x => x.startTime), name: "Start Time", columnSpan: new ColumnSpan(1, 2), onChange: (event: Event, field: FormField) => this.validateTime() }),
      new TimeFormField({ property: nameOf<TaskAssignment>(x => x.endTime), name: "End Time", columnSpan: new ColumnSpan(1, 2), onChange: (event: Event, field: FormField) => this.validateTime() }),

      new ArrayFormField({
        property: nameOf<TaskAssignment>(x => x.locationAssignments), name: "Locations",
        items: locationFields, cssClass: "multiple-rows", columnSpan: new ColumnSpan(1, 1), required: false
      }),
    ];
  }
}
